import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Grid,
  Typography,
  Card,
  Box,
  TextField,
  Select,
  MenuItem,
  makeStyles,
  Tooltip,
  IconButton,
  InputAdornment
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
import { useSnackbar } from 'notistack';
import { isEqual } from 'lodash';
import * as d3 from 'd3';
import NotUpdatedLengend from './NotUpdatedLengend';
import { _isNaN } from '../../util/customFunctions';
import { store } from '../../util/store';
import {
  updateQuotaWithOptimisticLock,
  updateTeamQuotaWithOptimisticLock,
  updateCompanyQuotaWithOptimisticLock
} from './redux/management.thunk';
import {
  selectFailedUpdates,
  selectSuccessfulUpdates,
  selectTeamFailedUpdates,
  selectTeamSuccessfulUpdates,
  selectCompanyFailedUpdates,
  selectCompanySuccessfulUpdates
} from './redux/management.slice';
import { QUOTA_VALIDATION, QUOTA_ERROR_MESSAGES } from './constants';

const useDataGridStyles = makeStyles((theme) => ({
  dataGridContainer: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    '& .MuiDataGrid-root': {
      border: '1px solid rgba(224, 224, 224, 1)',
      width: '100%',
    },
    '& .MuiDataGrid-window': {
      left: 0,
      right: 0,
      bottom: 0,
      position: 'absolute',
      overflowX: 'hidden',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#4472c4 !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold !important',
      color: 'white !important',
    },
    '& .MuiDataGrid-iconSeparator': {
      color: 'white !important',
    },
    '& .MuiDataGrid-sortIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-menuIcon': {
      color: 'white !important',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      color: 'white !important',
    },
    '& .MuiDataGrid-row': {
      '&:hover': {
        backgroundColor: '#e3ecfa !important',
      },
    },
    '& .MuiDataGrid-cell--editable': {
      backgroundColor: '#f5f5f5',
      '&:hover': {
        backgroundColor: '#e8e8e8',
      },
    },
    '& .edited-cell': {
      backgroundColor: '#fff3cd !important',
      border: '1px solid #ffc107 !important',
    },
    '& .not-updated-row': {
      backgroundColor: '#f4b183 !important',
    },
  },
  tableWrapper: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const MaintainenenceTableDataGrid = (props) => {
  const [rowData, setRowData] = useState([]);
  const [initialData, setInitialData] = useState([]);
  const [updatedObjectMap, setUpdatedObjectMap] = useState({});
  const [someUpdateFailed, setSomeUpdateFailed] = useState(false);
  const [disableUpdateButton, setDisableUpdateButton] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [containerHeight, setContainerHeight] = useState(500);
  const [pageSize, setPageSize] = useState(50);
  const [failedRowKeys, setFailedRowKeys] = useState(new Set()); // Track failed rows by key
  const [searchText, setSearchText] = useState(''); // Search text state

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const dataGridClasses = useDataGridStyles();
  const previousDataRef = React.useRef();

  const failedUpdates = useSelector(selectFailedUpdates);
  const successfulUpdates = useSelector(selectSuccessfulUpdates);
  const teamFailedUpdates = useSelector(selectTeamFailedUpdates);
  const teamSuccessfulUpdates = useSelector(selectTeamSuccessfulUpdates);
  const companyFailedUpdates = useSelector(selectCompanyFailedUpdates);
  const companySuccessfulUpdates = useSelector(selectCompanySuccessfulUpdates);

  const state = store.getState();
  const currencySymbol = state.app.user.Currency;

  // Get teams and ramp status from props or redux
  const teams = props.teams || [];
  const rampStatus = props.rampStatus || ['Ramped', 'Ramping', 'Not Ramped'];

  useEffect(() => {
    props.setTouched(false);
  }, []);

  useEffect(() => {
    if (!isEqual(props.data, previousDataRef.current)) {
      let data = props.data.map((it, idx) => ({ ...it, id: idx }));
      setRowData(data);
      setInitialData(props.data);
      previousDataRef.current = props.data;

      const FIXED_REPS = 10;
      let _containerHeight =
        60 + props.data.length * 52 <= window.innerHeight - 255
          ? 75 + props.data.length * 52
          : 60 + FIXED_REPS * 52;

      if (props.data.length === 0) {
        _containerHeight = 300;
      }
      setContainerHeight(_containerHeight);

      if (props.data !== null) {
        setIsDataLoaded(true);
      }
    }
  }, [props.data]);

  useEffect(() => {
    if (isDataLoaded) {
      enqueueSnackbar('Data loaded', { variant: 'success' });
    }
  }, [isDataLoaded]);

  useEffect(() => {
    if (Array.isArray(props.udpatedRecords)) {
      const isUpdatedArr = props.udpatedRecords.map((it) => it.isUpdated);
      setSomeUpdateFailed(Boolean(isUpdatedArr.indexOf(false) >= 0));
    }
    if (props.udpatedRecords !== null) {
      enqueueSnackbar('Data Updated', { variant: 'success' });
    }
  }, [props.udpatedRecords]);

  // Handle optimistic locking update results for rep quota
  useEffect(() => {
    if (props.tab === 'rep') {
      if (failedUpdates && failedUpdates.length > 0) {
        setSomeUpdateFailed(true);
        // Track failed rows by UserId + FiscalQuarter
        const newFailedKeys = new Set();
        failedUpdates.forEach(update => {
          newFailedKeys.add(`${update.UserId}_${update.FiscalQuarter}`);
        });
        setFailedRowKeys(newFailedKeys);
        // enqueueSnackbar(
        //   `${failedUpdates.length} rep quota update(s) failed due to concurrent modifications. Current values have been loaded.`,
        //   { variant: 'warning', autoHideDuration: 3000 }
        // );
      }
      if (successfulUpdates && successfulUpdates.length > 0) {
        enqueueSnackbar(
          `Updated Successfully`,
          { variant: 'success' }
        );
        // Clear the updated object map and failed rows for successfully updated records
        setUpdatedObjectMap({});
        setDisableUpdateButton(true);
        setFailedRowKeys(new Set());
        setSomeUpdateFailed(false);
      }
    }
  }, [failedUpdates, successfulUpdates, props.tab, enqueueSnackbar]);

  // Handle optimistic locking update results for team quota
  useEffect(() => {
    if (props.tab === 'team') {
      if (teamFailedUpdates && teamFailedUpdates.length > 0) {
        setSomeUpdateFailed(true);
        // Track failed rows by Team + FiscalQuarter
        const newFailedKeys = new Set();
        teamFailedUpdates.forEach(update => {
          newFailedKeys.add(`${update.Team}_${update.FiscalQuarter}`);
        });
        setFailedRowKeys(newFailedKeys);
        // enqueueSnackbar(
        //   `${teamFailedUpdates.length} team quota update(s) failed due to concurrent modifications. Current values have been loaded.`,
        //   { variant: 'warning', autoHideDuration: 3000 }
        // );
      }
      if (teamSuccessfulUpdates && teamSuccessfulUpdates.length > 0) {
        enqueueSnackbar(
          `Updated Successfully`,
          { variant: 'success' }
        );
        // Clear the updated object map and failed rows for successfully updated records
        setUpdatedObjectMap({});
        setDisableUpdateButton(true);
        setFailedRowKeys(new Set());
        setSomeUpdateFailed(false);
      }
    }
  }, [teamFailedUpdates, teamSuccessfulUpdates, props.tab, enqueueSnackbar]);

  // Handle optimistic locking update results for company quota
  useEffect(() => {
    if (props.tab === 'company') {
      if (companyFailedUpdates && companyFailedUpdates.length > 0) {
        setSomeUpdateFailed(true);
        // Track failed rows by FiscalQuarter
        const newFailedKeys = new Set();
        companyFailedUpdates.forEach(update => {
          newFailedKeys.add(update.FiscalQuarter);
        });
        setFailedRowKeys(newFailedKeys);
        // enqueueSnackbar(
        //   `${companyFailedUpdates.length} company quota update(s) failed due to concurrent modifications. Current values have been loaded.`,
        //   { variant: 'warning', autoHideDuration: 3000 }
        // );
      }
      if (companySuccessfulUpdates && companySuccessfulUpdates.length > 0) {
        enqueueSnackbar(
          `Updated Successfully`,
          { variant: 'success' }
        );
        // Clear the updated object map and failed rows for successfully updated records
        setUpdatedObjectMap({});
        setDisableUpdateButton(true);
        setFailedRowKeys(new Set());
        setSomeUpdateFailed(false);
      }
    }
  }, [companyFailedUpdates, companySuccessfulUpdates, props.tab, enqueueSnackbar]);

  const handleSave = () => {
    let updateData = [];
    for (let updated in updatedObjectMap) {
      const value = updatedObjectMap[updated];
      updateData = [...updateData, value];
    }

    // Clear all previous update failure tracking before submitting
    setSomeUpdateFailed(false);
    setFailedRowKeys(new Set());

    // Use the new optimistic locking endpoints for all tabs
    if (props.tab === 'rep') {
      dispatch(updateQuotaWithOptimisticLock({ updatedObjects: updateData, isDryRun: false }));
      enqueueSnackbar('Updating rep quota', { variant: 'info' });
    } else if (props.tab === 'team') {
      dispatch(updateTeamQuotaWithOptimisticLock({ updatedObjects: updateData, isDryRun: false }));
      enqueueSnackbar('Updating team quota', { variant: 'info' });
    } else if (props.tab === 'company') {
      dispatch(updateCompanyQuotaWithOptimisticLock({ updatedObjects: updateData, isDryRun: false }));
      enqueueSnackbar('Updating company quota', { variant: 'info' });
    }

    props.setTouched(false);
  };

  // Handle cell double-click to enable double-click editing for select fields
  const handleCellDoubleClick = useCallback((params, event) => {
    // Enable double-click editing for Team_Assignment and Ramp_Status fields
    if (params.field === 'Team_Assignment' || params.field === 'Ramp_Status') {
      if (!params.colDef.editable) return;
      // Start editing mode on double click using setCellMode
      params.api.setCellMode(params.id, params.field, 'edit');
    }
  }, []);

  // Custom edit component for numeric fields with 32-bit signed integer range validation
  const NumericEditCell = (params) => {
    const { id, field, value, api } = params;
    const [inputValue, setInputValue] = useState(value || '');

    const handleChange = (event) => {
      const newValue = event.target.value.replace(/[^\d-]/g, ''); // Only digits and minus sign

      // Allow empty value or just minus sign
      if (newValue === '' || newValue === '-') {
        setInputValue(newValue);
        api.setEditCellValue({ id, field, value: newValue });
        return;
      }

      // Validate the numeric value is within the defined quota validation range
      const parsedValue = parseInt(newValue, 10);

      // Check if it's a valid number and within the quota validation range
      if (!isNaN(parsedValue) && parsedValue >= QUOTA_VALIDATION.MIN_VALUE && parsedValue <= QUOTA_VALIDATION.MAX_VALUE) {
        setInputValue(newValue);
        api.setEditCellValue({ id, field, value: newValue });
      }
      // If the value is out of range or invalid, don't update the field (silently ignore)
    };

    return (
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={() => {
          api.commitCellChange({ id, field });
          api.setCellMode(id, field, 'view');
        }}
        autoFocus
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          outline: 'none',
          padding: '0 8px',
          fontSize: 'inherit',
          fontFamily: 'inherit',
        }}
      />
    );
  };

  // Handle cell edit commit
  const handleCellEditCommit = useCallback((params) => {
    props.setTouched(true);

    const { id, field, value: newValue } = params;
    const row = rowData.find(r => r.id === id);

    if (!row) return;

    // Find original data
    let oldObject;
    if (props.tab === 'team') {
      oldObject = initialData.find(
        (it) => it.Team === row.Team && it.FiscalQuarter === row.FiscalQuarter
      );
    } else if (props.tab === 'company') {
      oldObject = initialData.find(
        (it) => it.FiscalQuarter === row.FiscalQuarter
      );
    } else {
      oldObject = initialData.find(
        (it) => it.UserId === row.UserId && it.FiscalQuarter === row.FiscalQuarter
      );
    }

    if (!oldObject) return;

    const oldValue = oldObject[field]; // Use original DB value for optimistic locking

    // Check if the value has actually changed
    const currentValue = row[field];
    const hasChanged = currentValue !== newValue;

    // Validation - only apply to user-edited values (when value has changed)
    if ((field === 'Quota' || field === 'NQP_GOAL') && hasChanged) {
      const numValue = Number(newValue);
      const fieldName = field === 'Quota' ? 'Quota' : 'NQP goal';

      if (_isNaN(newValue) || numValue < 0 || newValue === '') {
        enqueueSnackbar(QUOTA_ERROR_MESSAGES.MUST_BE_POSITIVE(fieldName), { variant: 'error' });
        return;
      }
      // Check if the value contains decimal points (only for new user input)
      if (String(newValue).includes('.')) {
        enqueueSnackbar(QUOTA_ERROR_MESSAGES.NO_DECIMALS(fieldName), { variant: 'error' });
        return;
      }
      // Check if the value is within the defined quota validation range
      if (numValue < QUOTA_VALIDATION.MIN_VALUE || numValue > QUOTA_VALIDATION.MAX_VALUE) {
        enqueueSnackbar(QUOTA_ERROR_MESSAGES.OUT_OF_RANGE(fieldName), { variant: 'error' });
        return;
      }
    }

    // Update row data and clear any previous update failure flags
    const updatedRows = rowData.map(r => {
      if (r.id === id) {
        return {
          ...r,
          [field]: newValue,
          // Clear update failure flags when user edits the cell again
          updateFailed: undefined,
          updateSuccess: undefined,
          failureReason: undefined
        };
      }
      return r;
    });
    setRowData(updatedRows);

    // Track updated fields
    const previouslyUpdatedFields = updatedObjectMap[id]
      ? updatedObjectMap[id].updatedFields
      : [];

    let _updatedFields = [...new Set([...previouslyUpdatedFields])];
    const fieldUpdatedKey = `${field}_Updated`;
    const isChanged = oldObject[field] !== (typeof oldObject[field] === 'number' ? parseInt(newValue) : newValue);

    if (isChanged && !_updatedFields.includes(field)) {
      _updatedFields.push(field);
    } else if (!isChanged) {
      _updatedFields = _updatedFields.filter(f => f !== field);
    }

    let updateObject = {
      ...row,
      [field]: newValue,
      [`new${field}`]: typeof oldObject[field] === 'number' ? parseInt(newValue) : newValue,
      [`old${field}`]: oldValue,
      updatedFields: _updatedFields,
      isRamp_Status_Updated: field === 'Ramp_Status',
      [fieldUpdatedKey]: isChanged,
    };

    if (_updatedFields.length === 0) {
      // Remove from updated map if no changes
      const newMap = { ...updatedObjectMap };
      delete newMap[id];
      setUpdatedObjectMap(newMap);
      setDisableUpdateButton(Object.keys(newMap).length === 0);
    } else {
      setUpdatedObjectMap({
        ...updatedObjectMap,
        [id]: { ...updatedObjectMap[id], ...updateObject },
      });
      setDisableUpdateButton(false);
    }
  }, [rowData, initialData, updatedObjectMap, props]);

  // CSV Export - use filtered data
  const handleExportCSV = () => {
    const headers = columns.map(col => col.headerName || col.field).join(',');
    const csvRows = filteredRowData.map(row => {
      return columns.map(col => {
        const field = col.field;
        let value = row[field];

        if (col.valueFormatter && value !== undefined && value !== null) {
          value = col.valueFormatter({ value });
        }

        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }

        return value;
      }).join(',');
    });

    const csvContent = [headers, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${props.title || 'table-data'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Define columns based on tab
  const columns = useMemo(() => {
    if (props.tab === 'rep') {
      return [
        {
          field: 'FullName',
          headerName: 'Full Name',
          flex: 1.8,
          minWidth: 150,
          editable: false,
        },
        {
          field: 'FiscalQuarter',
          headerName: 'Fiscal Quarter',
          flex: 0.9,
          minWidth: 110,
          editable: false,
          align: 'left',
          headerAlign: 'left',
        },
        {
          field: 'Team_Assignment',
          headerName: 'Team',
          flex: 1.5,
          minWidth: 130,
          editable: true,
          type: 'singleSelect',
          // Handle both array of strings and array of {value, label} objects
          valueOptions: Array.isArray(teams) && teams.length > 0 && typeof teams[0] === 'object'
            ? teams
            : teams.map(t => ({ value: t, label: t })),
          valueFormatter: (params) => {
            // Display the actual team name (without indentation) in the cell
            return params.value || '';
          },
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.Team_Assignment_Updated ? 'edited-cell' : '';
          },
        },
        {
          field: 'Quota',
          headerName: 'Quota',
          flex: 1.1,
          minWidth: 110,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          renderEditCell: NumericEditCell,
          valueFormatter: (params) => `${currencySymbol} ${d3.format(',')(Math.round(params.value || 0))}`,
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.Quota_Updated ? 'edited-cell' : '';
          },
        },
        {
          field: 'NQP_GOAL',
          headerName: 'NQP Goal',
          tooltipField: 'NQP_GOAL',
          flex: 1.1,
          minWidth: 110,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          renderEditCell: NumericEditCell,
          valueFormatter: (params) => `${currencySymbol} ${d3.format(',')(Math.round(params.value || 0))}`,
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.NQP_GOAL_Updated ? 'edited-cell' : '';
          },
        },
        {
          field: 'Ramp_Status',
          headerName: 'Ramp Status',
          flex: 1,
          minWidth: 110,
          editable: true,
          type: 'singleSelect',
          valueOptions: rampStatus,
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.Ramp_Status_Updated ? 'edited-cell' : '';
          },
        },
      ];
    } else if (props.tab === 'team') {
      return [
        {
          field: 'Team',
          headerName: 'Team',
          flex: 1.5,
          minWidth: 150,
          editable: false,
        },
        {
          field: 'FiscalQuarter',
          headerName: 'Fiscal Quarter',
          flex: 1,
          minWidth: 130,
          editable: false,
          align: 'left',
          headerAlign: 'left',
        },
        {
          field: 'Quota',
          headerName: 'Quota',
          flex: 1.2,
          minWidth: 130,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          renderEditCell: NumericEditCell,
          valueFormatter: (params) => `${currencySymbol} ${d3.format(',')(Math.round(params.value || 0))}`,
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.Quota_Updated ? 'edited-cell' : '';
          },
        },
        {
          field: 'NQP_GOAL',
          headerName: 'NQP Goal',
          tooltipField: 'NQP_GOAL',
          flex: 1.2,
          minWidth: 130,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          renderEditCell: NumericEditCell,
          valueFormatter: (params) => `${currencySymbol} ${d3.format(',')(Math.round(params.value || 0))}`,
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.NQP_GOAL_Updated ? 'edited-cell' : '';
          },
        },
      ];
    } else {
      // company
      return [
        {
          field: 'FiscalQuarter',
          headerName: 'Fiscal Quarter',
          flex: 1,
          minWidth: 150,
          editable: false,
          align: 'left',
          headerAlign: 'left',
        },
        {
          field: 'Quota',
          headerName: 'Quota',
          flex: 1,
          minWidth: 150,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          renderEditCell: NumericEditCell,
          valueFormatter: (params) => `${currencySymbol} ${d3.format(',')(Math.round(params.value || 0))}`,
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.Quota_Updated ? 'edited-cell' : '';
          },
        },
        {
          field: 'NQP_GOAL',
          headerName: 'NQP Goal',
          tooltipField: 'NQP_GOAL',
          flex: 1,
          minWidth: 200,
          editable: true,
          type: 'number',
          align: 'right',
          headerAlign: 'right',
          renderEditCell: NumericEditCell,
          valueFormatter: (params) => `${currencySymbol} ${d3.format(',')(Math.round(params.value || 0))}`,
          cellClassName: (params) => {
            return updatedObjectMap[params.id]?.NQP_GOAL_Updated ? 'edited-cell' : '';
          },
        },
      ];
    }
  }, [props.tab, teams, rampStatus, currencySymbol, updatedObjectMap, NumericEditCell]);

  // Filter rows based on search text
  const filteredRowData = useMemo(() => {
    if (!searchText) return rowData;

    const searchLower = searchText.toLowerCase();
    return rowData.filter((row) => {
      if (props.tab === 'rep') {
        return (
          row.FullName?.toLowerCase().includes(searchLower) ||
          row.FiscalQuarter?.toLowerCase().includes(searchLower) ||
          row.Team_Assignment?.toLowerCase().includes(searchLower) ||
          row.Ramp_Status?.toLowerCase().includes(searchLower)
        );
      } else if (props.tab === 'team') {
        return (
          row.Team?.toLowerCase().includes(searchLower) ||
          row.FiscalQuarter?.toLowerCase().includes(searchLower)
        );
      } else {
        // company
        return (
          row.FiscalQuarter?.toLowerCase().includes(searchLower)
        );
      }
    });
  }, [rowData, searchText, props.tab]);

  // Get row class name for failed updates
  const getRowClassName = (params) => {
    // Build row key based on tab type
    let rowKey;
    if (props.tab === 'rep') {
      rowKey = `${params.row.UserId}_${params.row.FiscalQuarter}`;
    } else if (props.tab === 'team') {
      rowKey = `${params.row.Team}_${params.row.FiscalQuarter}`;
    } else if (props.tab === 'company') {
      rowKey = params.row.FiscalQuarter;
    }

    // Check if this row failed in the last update
    if (failedRowKeys.has(rowKey)) {
      return 'not-updated-row';
    }

    return '';
  };

  return (
    <Grid container>
      <Grid
        xs={12}
        container
        item
        component={Card}
        style={{ marginBottom: 10, maxWidth: '1000px', padding: '24px 24px' }}
      >
        <Grid item xs={12} container direction="column" alignItems="flex-start">
          <Box display="flex" alignItems="center" gap={1} style={{ marginBottom: '16px', width: '100%' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={
                props.tab === 'rep'
                  ? "Search by name, fiscal quarter, team, or ramp status..."
                  : props.tab === 'team'
                  ? "Search by team or fiscal quarter..."
                  : "Search by fiscal quarter..."
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Download as CSV" arrow>
              <IconButton
                onClick={handleExportCSV}
                style={{
                  padding: '8px',
                  margin: '0 8px',
                }}
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <div
            className={dataGridClasses.tableWrapper}
            style={{
              height: containerHeight,
              minHeight: '400px',
              maxHeight: '50vh',
              width: '100%',
              maxWidth: '1000px',
            }}
          >
            <div className={dataGridClasses.dataGridContainer}>
              <DataGrid
                rows={filteredRowData}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 25, 50, 100]}
                pagination
                disableSelectionOnClick
                onCellDoubleClick={handleCellDoubleClick}
                onCellEditCommit={handleCellEditCommit}
                getRowClassName={getRowClassName}
                components={{
                  NoRowsOverlay: () => (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography variant="body1" color="textSecondary">
                        No data available
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </div>
          </div>
        </Grid>
      </Grid>

      {/* Sticky Footer with Update Button */}
      <Grid
        item
        xs={12}
        container
        justifyContent="flex-start"
        component={Card}
        elevation={4}
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 999,
          padding: '10px 0px 10px 20px',
        }}
      >
        <Grid item container>
          <Grid item xs={2} container style={{ padding: '0.5rem' }}>
            <Button
              disabled={disableUpdateButton}
              onClick={handleSave}
              variant="contained"
              color="primary"
              align="center"
            >
              Update
            </Button>
          </Grid>

          <Grid item xs={10}>
            {someUpdateFailed && (
              <Grid item container xs={12} justifyContent="center">
                <NotUpdatedLengend
                  text={
                    'A cell in this row was recently changed by another user. Please review the current values and submit your update again.'
                  }
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MaintainenenceTableDataGrid;
